import { createSlice } from "@reduxjs/toolkit"
import { withFirestoreHydration } from "../middleware/firestorePersistence"

// Initial state for budget slice
const initialState = {
  transactions: {},
  categories: {},
  budgets: {},
  monthlyTotals: {},
  selectedMonth: new Date().toISOString().slice(0, 7).replace("-", ""), // YYYYMM format
  isLoading: false,
  error: null,
  _hydrated: false,
}

const budgetSlice = createSlice({
  name: "budget",
  initialState,
  reducers: {
    // Transaction management
    setTransactions: (state, action) => {
      const { month, transactions } = action.payload
      state.transactions[month] = transactions
      state.isLoading = false
    },

    addTransaction: (state, action) => {
      const { month, transaction } = action.payload
      if (!state.transactions[month]) {
        state.transactions[month] = {}
      }
      state.transactions[month][transaction.id] = transaction
    },

    updateTransaction: (state, action) => {
      const { month, transactionId, updates } = action.payload
      if (
        state.transactions[month] &&
        state.transactions[month][transactionId]
      ) {
        state.transactions[month][transactionId] = {
          ...state.transactions[month][transactionId],
          ...updates,
        }
      }
    },

    deleteTransaction: (state, action) => {
      const { month, transactionId } = action.payload
      if (state.transactions[month]) {
        delete state.transactions[month][transactionId]
      }
    },

    // Category management
    setCategories: (state, action) => {
      state.categories = action.payload
    },

    addCategory: (state, action) => {
      const category = action.payload
      state.categories[category.id] = category
    },

    updateCategory: (state, action) => {
      const { categoryId, updates } = action.payload
      if (state.categories[categoryId]) {
        state.categories[categoryId] = {
          ...state.categories[categoryId],
          ...updates,
        }
      }
    },

    deleteCategory: (state, action) => {
      const categoryId = action.payload
      delete state.categories[categoryId]
    },

    // Budget management
    setBudgets: (state, action) => {
      const { month, budgets } = action.payload
      state.budgets[month] = budgets
    },

    setBudgetAmount: (state, action) => {
      const { month, categoryId, amount } = action.payload
      if (!state.budgets[month]) {
        state.budgets[month] = {}
      }
      state.budgets[month][categoryId] = amount
    },

    deleteBudget: (state, action) => {
      const { month, categoryId } = action.payload
      if (state.budgets[month]) {
        delete state.budgets[month][categoryId]
      }
    },

    // Monthly totals
    setMonthlyTotals: (state, action) => {
      const { month, totals } = action.payload
      state.monthlyTotals[month] = totals
    },

    // UI state
    setSelectedMonth: (state, action) => {
      state.selectedMonth = action.payload
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
      state.isLoading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Firestore hydration (handled by middleware)
    hydrateFromFirestore: (state, action) => {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    },
  },
})

// Export actions
export const {
  setTransactions,
  addTransaction,
  updateTransaction,
  deleteTransaction,
  setCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  setBudgets,
  setBudgetAmount,
  deleteBudget,
  setMonthlyTotals,
  setSelectedMonth,
  setLoading,
  setError,
  clearError,
  hydrateFromFirestore,
} = budgetSlice.actions

// Selectors
export const selectTransactions = (state) => state.budget.transactions
export const selectTransactionsForMonth = (state, month) =>
  state.budget.transactions[month] || {}
export const selectCategories = (state) => state.budget.categories
export const selectBudgets = (state) => state.budget.budgets
export const selectBudgetsForMonth = (state, month) =>
  state.budget.budgets[month] || {}
export const selectMonthlyTotals = (state) => state.budget.monthlyTotals
export const selectMonthlyTotalsForMonth = (state, month) =>
  state.budget.monthlyTotals[month] || {}
export const selectSelectedMonth = (state) => state.budget.selectedMonth
export const selectBudgetLoading = (state) => state.budget.isLoading
export const selectBudgetError = (state) => state.budget.error
export const selectBudgetHydrated = (state) => state.budget._hydrated

// Enhanced reducer with Firestore hydration
export default withFirestoreHydration(budgetSlice.reducer)
